import {
  CustomerDetailsStep,
  AddressStep,
  InfoStep,
  AdditionalInfoStep,
  ValueStep,
  TimeframeStep,
  MatchingStep,
  SuccessStep,
  RadioStep,
  SelectStep,
  AboutSaleStep,
  FinalStep,
} from "../components/Steps";
import { UTMParams } from "../utils";
import { PropertySeriousIssuesOptions } from "./options";
import {
  detectContactDetailsFailedField,
  getQualificationParams,
} from "./tracking";

type AdditionalButton = {
  label: string;
  onClick: () => void;
};

export type LeadFormApi = {
  step: number;
  setStep: (step: number) => void;

  responses: Record<string, unknown>;
  setResponses: React.Dispatch<React.SetStateAction<Record<string, unknown>>>;

  utms: UTMParams;
  setUTMs: React.Dispatch<React.SetStateAction<UTMParams>>;

  userPath: string;
  setUserPath: React.Dispatch<React.SetStateAction<string>>;

  sessionKey: string;
  setSessionKey: React.Dispatch<React.SetStateAction<string>>;

  loading: boolean;
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;
};

export type StepConfig = {
  name: string;
  title?: string;
  altTitle?: string;
  description?: string;
  statekey?: string;
  pulse?: boolean;
  component: React.ComponentType<any>;
  nextStep?: (args: { value: any; form: LeadFormApi }) => void;
  prevStep?: (args: { form: LeadFormApi }) => void;
  validate?: boolean;
  validateFunc?: (form: LeadFormApi) => boolean;
  additionalProps?: { [key: string]: any };
  additionalButtons?: Array<AdditionalButton>;
  nextLabel?: string;
  next?: boolean;
  back?: boolean;
  detectFailedField?: (responses: Record<string, unknown>) => {
    fieldName: string;
    errorType: string;
  };
  getStepCompleteExtraParams?: (
    responses: Record<string, unknown>,
  ) => Record<string, any>;
};

const stepsConfig: Array<StepConfig> = [
  {
    name: "Address Input",
    component: AddressStep,
    statekey: "address",
    validateFunc: function (form) {
      if (
        form.responses.line1 !== undefined &&
        form.responses.line1 !== "" &&
        form.responses.town !== undefined &&
        form.responses.town !== "" &&
        form.responses.postcode !== undefined &&
        form.responses.postcode !== ""
      ) {
        return true;
      }
      return false;
    },
    back: false,
    next: false,
  },
  {
    name: "Property Details Review",
    component: InfoStep,
    description: "Confirm your property details",
    statekey: "property_info",
    validate: true,
    validateFunc: function (form) {
      if (
        form.responses.property_type !== undefined &&
        form.responses.property_type !== "" &&
        form.responses.bedrooms !== undefined &&
        form.responses.bedrooms !== "" &&
        form.responses.bathrooms !== undefined &&
        form.responses.bathrooms !== "" &&
        form.responses.reception_rooms !== undefined &&
        form.responses.reception_rooms !== "" &&
        form.responses.floor_area_sqm !== undefined &&
        form.responses.floor_area_sqm !== "" &&
        form.responses.year_built !== undefined &&
        form.responses.year_built !== "" &&
        form.responses.freehold_leasehold !== undefined &&
        form.responses.freehold_leasehold !== "" &&
        form.responses.council_tax_band !== undefined &&
        form.responses.council_tax_band !== "" &&
        form.responses.epc_rating !== undefined &&
        form.responses.epc_rating !== "" &&
        form.responses.primary_heating !== undefined &&
        form.responses.primary_heating !== ""
      ) {
        return true;
      } else {
        return false;
      }
    },
    back: false,
  },
  {
    name: "Contact Details",
    component: CustomerDetailsStep,
    description:
      "We'll ask a few details so we can compare cash buyers that match your property and circumstances.",
    statekey: "value",
    back: false,
    validateFunc: function (form) {
      if (
        form.responses.title !== undefined &&
        form.responses.title !== "" &&
        form.responses.first_name !== undefined &&
        form.responses.first_name !== "" &&
        form.responses.last_name !== undefined &&
        form.responses.last_name !== "" &&
        form.responses.email !== undefined &&
        form.responses.email !== "" &&
        form.responses.phone_number !== undefined &&
        form.responses.phone_number !== ""
      ) {
        return true;
      }
      return false;
    },
    detectFailedField: detectContactDetailsFailedField,
  },
  {
    name: "Property Situation",
    component: AdditionalInfoStep,
    description: "Confirm your property details",
    statekey: "property_additional_info",
    nextStep: function ({ form }) {
      if (form.responses.serious_issues === "yes") {
        form.setStep(form.step + 1);
      } else {
        form.setStep(form.step + 2);
      }
    },
    validate: true,
    validateFunc: function (form) {
      if (
        form.responses.inherited !== "" &&
        form.responses.inherited !== undefined &&
        form.responses.property_condition !== "" &&
        form.responses.property_condition !== undefined &&
        form.responses.serious_issues !== "" &&
        form.responses.serious_issues !== undefined &&
        form.responses.occupation_status !== "" &&
        form.responses.occupation_status !== undefined &&
        form.responses.user_estimated_value !== "" &&
        form.responses.user_estimated_value !== undefined
      ) {
        return true;
      }
      return false;
    },
    getStepCompleteExtraParams: getQualificationParams,
  },
  {
    name: "Property Issue Type",
    title: "Are any of these issues affecting the property?",
    description:
      "Some specialist buyers actively look for properties with these issues.",
    component: SelectStep,
    statekey: "serious_issues_type",
    validate: true,
    additionalProps: {
      options: PropertySeriousIssuesOptions,
      perRow: 2,
    },
  },
  {
    name: "Cash Sale Motivation",
    title: "About your cash sale",
    description:
      "These final details help us identify the buyers most suitable for your situation.",
    component: AboutSaleStep,
    statekey: "about_sale",
    prevStep: function ({ form }) {
      if (form.responses.serious_issues === "yes") {
        form.setStep(form.step - 1);
      } else {
        form.setStep(form.step - 2);
      }
    },
    validateFunc: function (form) {
      if (
        form.responses.cash_sale_reason !== "" &&
        form.responses.cash_sale_reason !== undefined &&
        form.responses.sale_reason !== "" &&
        form.responses.sale_reason !== undefined &&
        form.responses.cash_sale_understanding !== "" &&
        form.responses.cash_sale_understanding !== undefined
      ) {
        return true;
      }
      return false;
    },
    back: true,
  },
  {
    name: "Sale Timeframe",
    title: "How soon would you like to sell your property?",
    description:
      "We'll prioritise buyers who can complete within your preferred timeframe.",
    component: TimeframeStep,
    statekey: "timeframe",
    validateFunc: function (form) {
      if (
        form.responses.timeframe !== "" &&
        form.responses.timeframe !== undefined
      ) {
        return true;
      }
      return false;
    },
    back: true,
  },
  {
    name: "Buyer Matching Buffer",
    component: MatchingStep,
    statekey: "matching",
    next: false,
    back: false,
  },
  {
    name: "Contact Confirmation",
    component: FinalStep,
    statekey: "final",
    next: false,
    validate: false,
    nextLabel: "See your comparison",
    back: false,
  },
];

export default stepsConfig;
